import { useAuth } from "@/context/AuthContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PERM, hasGlobalPermission } from "@/lib/permissions";

// Dealer.card_versions keys (see models.CARD_NAMES) -> human label.
const CARD_LABELS = {
  vps: "Main contact",
  opening_hours: "Opening hours",
  legal_pages: "Legal pages",
  social_media: "Social media",
  certifications_sales_centrum: "Certifications & Sales Centrum",
  onetrust: "Cookie consents (OneTrust)",
  web_modules: "Web modules",
  custom_data: "Custom data",
  employees: "Employees",
};

/**
 * Small "v" marker showing a card's data version (Dealer.card_versions[card])
 * on hover — a counter bumped whenever a tracked field's value actually
 * changes (see backend dealer_versioning.py). Visible only to holders of
 * system.dev_tools (same gate as FeedPathTag); renders nothing otherwise.
 */
export function VersionTag({ version, testId }) {
  const { user } = useAuth();
  if (version == null || !hasGlobalPermission(user, PERM.SYSTEM_DEV_TOOLS)) return null;

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={testId || "version-tag"}
            className="inline-flex h-4 w-4 shrink-0 cursor-default items-center justify-center rounded-sm border border-muted-foreground/40 font-mono text-[10px] italic leading-none text-muted-foreground/70 hover:text-foreground"
          >
            v
          </span>
        </TooltipTrigger>
        <TooltipContent className="font-mono text-[11px]">Data version: {version}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Whole-dealer version (sum of all card counters), shown inline next to the
 * dealer info; hover lists the per-card breakdown. */
export function DealerVersionTag({ dealer }) {
  const { user } = useAuth();
  if (dealer?.version == null || !hasGlobalPermission(user, PERM.SYSTEM_DEV_TOOLS)) return null;

  const cards = dealer.card_versions || {};
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid="dealer-version-tag"
            className="inline-flex cursor-default items-center gap-1 rounded-sm border border-muted-foreground/40 px-1.5 py-0.5"
          >
            Data version: v{dealer.version}
          </span>
        </TooltipTrigger>
        <TooltipContent className="font-mono text-[11px]">
          <div className="mb-1 font-semibold">Dealer data version: {dealer.version}</div>
          {Object.entries(CARD_LABELS).map(([key, label]) => (
            <div key={key} className="flex justify-between gap-4">
              <span>{label}</span>
              <span>{cards[key] ?? 0}</span>
            </div>
          ))}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
